import {useQuery, useMutation} from '@apollo/client'
import {useIntl} from 'react-intl'
import {ORG_MESSAGES as M} from '../organizationMessages'
import {Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {
  GET_ORGANIZATION_EMAIL_SETTINGS,
  GET_RESOLVED_EMAIL_SETTINGS,
} from '../../../gql/queries/emailSettingsQuery'
import {SEND_TEST_EMAIL, UPDATE_ORGANIZATION_EMAIL_SETTINGS} from '../../../gql/mutations/emailSettingsMutations'
import {EmailSettingsForm, IEmailSettingsValues} from '../../shared/EmailSettingsForm'

interface Props {
  organizationId: string
}

export const OrganizationEmailSettingsTab: React.FC<Props> = ({organizationId}) => {
  const {data, loading, refetch} = useQuery(GET_ORGANIZATION_EMAIL_SETTINGS, {
    variables: {organizationId},
    fetchPolicy: 'network-only',
  })
  const {data: resolvedData, refetch: refetchResolved} = useQuery(GET_RESOLVED_EMAIL_SETTINGS, {
    variables: {organizationId},
    fetchPolicy: 'network-only',
  })
  const [updateOrganizationEmailSettings, {loading: saving}] = useMutation(UPDATE_ORGANIZATION_EMAIL_SETTINGS)
  const [sendTestEmail, {loading: sendingTestEmail}] = useMutation(SEND_TEST_EMAIL)

  const settings = data?.organizationEmailSettings
  const resolved = resolvedData?.resolvedEmailSettings

  const intl = useIntl()

  const handleSendTestEmail = async (toEmail: string) => {
    try {
      const res = await sendTestEmail({variables: {toEmail, organizationId}})
      const result = res.data?.sendTestEmail
      if (!result?.success) {
        toast.error(result?.errors?.map((e: any) => e.message).join('\n') || intl.formatMessage(M.errTestEmail))
        return
      }
      toast.success(intl.formatMessage(M.toastTestEmailSent, {email: toEmail}))
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errTestEmail))
    }
  }

  const handleSave = async (input: IEmailSettingsValues) => {
    try {
      const res = await updateOrganizationEmailSettings({variables: {organizationId, input}})
      const result = res.data?.updateOrganizationEmailSettings
      if (!result?.success) {
        toast.error(result?.errors?.map((e: any) => e.message).join('\n') || intl.formatMessage(M.errSavingEmailSettings))
        return
      }
      toast.success(intl.formatMessage(M.toastEmailSettingsSaved))
      refetch()
      refetchResolved()
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errSavingEmailSettings))
    }
  }

  if (loading) {
    return (
      <div className='text-center py-6'>
        <Spinner animation='border' size='sm' />
      </div>
    )
  }

  return (
    <div>
      <p className='text-muted mb-4' style={{fontSize: '0.85rem'}}>
        {intl.formatMessage(M.emailSettingsHint)}
      </p>
      <EmailSettingsForm
        values={settings}
        resolved={resolved}
        showInheritance
        saving={saving}
        onSave={handleSave}
        onSendTestEmail={handleSendTestEmail}
        sendingTestEmail={sendingTestEmail}
      />
    </div>
  )
}
